import React, { useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useLobby } from '@/hooks/useLobby';
import { usePageBackground } from '@/hooks/usePageBackground';
import { useShop } from '@/hooks/useShop';
import useCriticalErrorStore from '@store/useCriticalErrorStore';
import useConfigStore from '@store/useConfigStore';
import useShortcutContext from '@/hooks/useShortcutContext';
import { PlayerStatusInGame, PlayerType } from "@utils/enums";
import { User, Users, Swords, ShoppingCart, Bot, Shield, Heart, Zap, Coins, HandCoins, Flame, Target, Timer, Radio, Gem, Bolt, Loader2, Settings, ChevronDown, ChevronRight, Crosshair } from 'lucide-react';
import Tabs from '@/components/ui/Tabs/Tabs';
import { STAT_ICONS } from '@/utils/statIcons';
import ShopStatDisplay from '@components/ui/general/ShopStatDisplay';
import MainMenu from '@components/ui/game/main-menu/MainMenu';
import Loader from '@/components/ui/general/loader/Loader';
import CriticalErrorBanner from '@components/ui/general/CriticalErrorBanner';
import UnitImage from '@components/game/objects/UnitImage';
import XhelosTooltip from '@components/ui/general/xhelos-tooltip/XhelosTooltip';
import BotConfigModal from '@components/ui/game/lobby/BotConfigModal';
import LoadoutSelector from '@components/ui/game/lobby/LoadoutSelector';
import HumanConfigModal from '@components/ui/game/lobby/HumanConfigModal';
import { formatActionDetails, formatEffectDetails, parseActionTag, renderIconTag, getDamageTagTooltip } from '@/utils/unitStatHelpers';
import { getAoeInfo } from '@/utils/statIcons';
import { getCategoryIcon, getCategoryMeta } from '@/utils/loadoutHelpers';
import { useT } from '@/i18n';
import AudioManager from '@audio/AudioManager';

import './LobbyView.css';

function LobbyView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams();
  const t = useT();

  // Determine initial tab from URL (shop if path ends with /shop)
  const getInitialTab = () => location.pathname.endsWith('/shop') ? 'shop' : 'lobby';
  const [activeTab, setActiveTab] = React.useState(getInitialTab);

  // Handler to change tab and update URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'shop') {
      navigate(`/lobby/${gameId}/shop`, { replace: true });
    } else {
      navigate(`/lobby/${gameId}`, { replace: true });
    }
  };
  const [selectedUnitTemplate, setSelectedUnitTemplate] = React.useState(null);

  // Individual accordion states - track which action/effect IDs are expanded
  const [expandedActionIds, setExpandedActionIds] = React.useState(new Set());
  const [expandedEffectIds, setExpandedEffectIds] = React.useState(new Set());

  const toggleAction = (actionId) => {
    setExpandedActionIds(prev => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  const toggleEffect = (effectId) => {
    setExpandedEffectIds(prev => {
      const next = new Set(prev);
      if (next.has(effectId)) {
        next.delete(effectId);
      } else {
        next.add(effectId);
      }
      return next;
    });
  };

  // Critical error state (standby mode)
  const hasCriticalError = useCriticalErrorStore((state) => state.hasCriticalError);

  const {
    isLoading,
    isStarting,
    error,
    countdown,
    lobbyState,
    players,
    playerRosters,
    currentUserPlayer,
    isCreator,
    allHumanPlayersReady,
    handleReadyClick,
    handleStartGameClick,
    // Bot config
    aiPlayer,
    currentBotConfig,
    handleUpdateBotConfig,
    isUpdatingBotConfig,
    // AI loadout templates
    aiLoadoutTemplates,
    isAILoadoutLoading,
  } = useLobby({
    onCountdownEnd: () => navigate(`/games/${gameId}`)
  });

  // State for bot config modal
  const [isBotConfigModalOpen, setIsBotConfigModalOpen] = React.useState(false);

  // State for human config modal (loadout)
  const [isHumanConfigModalOpen, setIsHumanConfigModalOpen] = React.useState(false);

  // State for regenerate (separate from save to not close modal)
  const [isRegenerating, setIsRegenerating] = React.useState(false);

  // Shortcut: ESC opens settings (when no modal is open)
  const openSettings = useConfigStore((state) => state.openModal);
  const hasAnyModalOpen = isBotConfigModalOpen || isHumanConfigModalOpen;
  useShortcutContext(
    'page:lobby',
    { cancel: useCallback(() => openSettings(), [openSettings]) },
    !hasAnyModalOpen
  );

  const handleBotConfigSave = (config) => {
    handleUpdateBotConfig(config);
    // No cerrar modal - instant-apply pattern (igual que Human)
  };

  const handleBotConfigRegenerate = async (config) => {
    setIsRegenerating(true);
    await handleUpdateBotConfig(config);
    setIsRegenerating(false);
    // Don't close modal - just regenerate
  };

  const {
    shopData,
    isLoading: isShopLoading,
    purchasedData,
    isPurchasedLoading,
    loadoutData,
    isLoadoutLoading,
    handleBuyUnit,
    handleRefundUnit,
    handleRefundAllUnits,
    handleApplyLoadout,
    isBuying,
    isRefunding,
    isRefundingAll,
    isApplyingLoadout,
    // Category management (SSoT from backend)
    activeCategory,
    appliedLoadoutId,
    handleClearRoster,
    handleRegenerateRandom,
    handleSwitchToCustom,
    isSwitchingToCustom,
    // CRUD for custom loadout templates
    handleCreateLoadout,
    handleUpdateLoadout,
    handleDeleteLoadout,
    isCreatingLoadout,
    isUpdatingLoadout,
    isDeletingLoadout,
    maxCustomTemplates,
  } = useShop(gameId);

  // Extract loadout templates from API (response is already unwrapped by interceptor + shopApi)
  const loadoutTemplates = loadoutData?.loadout_templates || [];

  // Extraer los unit templates disponibles del shopData
  const availableTemplates = shopData?.available_units || [];

  // Extraer units compradas
  const purchasedUnits = purchasedData?.purchased_units || [];

  // Get max_units from API response (no fallback - fail loudly)
  const maxUnits = purchasedData?.max_units ?? shopData?.max_units;
  const isAtMaxUnits = purchasedUnits.length >= maxUnits;

  // Handler to remove one unit by template name (for editable UnitsPreview)
  const handleRemoveOneUnit = (templateName) => {
    const unitToRemove = purchasedUnits.find(
      pu => pu.unit?.unit_template?.name === templateName
    );
    if (unitToRemove) {
      handleRefundUnit(unitToRemove.unit.id);
    }
  };

  // Validar si el jugador tiene el mínimo de unidades requeridas
  const hasMinimumUnits = purchasedUnits.length >= 1;

  // Obtener el dinero actual del jugador
  const playerMoney = currentUserPlayer?.pivot?.money || 0;

  /**
   * Función centralizada para determinar si se puede iniciar el juego
   * Esta función es la única fuente de verdad para habilitar/deshabilitar el botón "Start Game"
   *
   * @returns {boolean} true si el juego puede iniciarse, false en caso contrario
   */
  const canStartGame = React.useMemo(() => {
    // Validación 1: Todos los jugadores humanos deben estar "Ready" (LOBBIED)
    if (!allHumanPlayersReady) {
      return false;
    }

    // Validación 2: El jugador actual (creador) debe tener al menos 1 unidad comprada
    // Nota: El backend validará que TODOS los jugadores tengan unidades,
    // pero verificamos el creador aquí para mejor UX
    if (!hasMinimumUnits) {
      return false;
    }

    // TODO: Agregar más validaciones aquí según sea necesario
    // Ejemplos futuros:
    // - Validar configuraciones de juego
    // - Validar mapas seleccionados
    // - Validar equipos balanceados
    // - etc.

    return true;
  }, [allHumanPlayersReady, hasMinimumUnits]);

  // Set default selected unit template to first available
  // IMPORTANT: This useEffect must be called before any early returns
  React.useEffect(() => {
    if (availableTemplates.length > 0 && !selectedUnitTemplate) {
      setSelectedUnitTemplate(availableTemplates[0].unit_template);
    }
  }, [availableTemplates, selectedUnitTemplate]);

  // Set lobby background via Asset system
  usePageBackground('bg.lobby');

  // Play main theme music if nothing is playing
  React.useEffect(() => {
    AudioManager.playMusicIfSilent('maintheme');
  }, []);

  /**
   * Render unit roster icons for a player
   * Shows each unit individually
   */
  const renderPlayerRosterIcons = (playerId) => {
    const roster = playerRosters[playerId] || [];
    if (roster.length === 0) return null;

    return (
      <div className="player-roster-icons">
        {roster.map((unit) => (
          <XhelosTooltip
            key={unit.id}
            content={unit.name}
            placement="top"
            theme="dark"
            trigger="hover"
          >
            <div className="roster-unit-icon">
              <UnitImage unitTemplate={{ name: unit.name, visual_data: unit.visual_data }} size="small" />
            </div>
          </XhelosTooltip>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <MainMenu />
        <Loader message={t('lobby.loading')} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <MainMenu />
        <div className="error-message">{error?.userMessage || t('error.could_not_load_lobby')}</div>
      </>
    );
  }

  if (!lobbyState) {
    return (
      <>
        <MainMenu />
        <Loader message={t('lobby.waiting')} />
      </>
    );
  }

  const team1 = players.filter(p => p.pivot.team === 1);
  const team2 = players.filter(p => p.pivot.team === 2);

  return (
    <div className={`main-container main-container--lobby ${hasCriticalError ? 'main-container--standby' : ''}`}>
      {/* Critical Error Banner - renders on top of everything */}
      <CriticalErrorBanner />
      {hasCriticalError && <div className="standby-overlay" />}

      <MainMenu />

      {/* Tab Navigation */}
      <Tabs
        variant="underline"
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={[
          {
            id: 'lobby',
            label: t('lobby.lobby_tab'),
            icon: <User size={16} />,
            content: null
          },
          {
            id: 'shop',
            label: (
              <>
                {t('lobby.shop_tab')}
                <span className={`tab-badge ${purchasedUnits.length === 0 ? 'tab-badge-warning' : ''}`}>{purchasedUnits.length}</span>
              </>
            ),
            icon: <ShoppingCart size={16} />,
            content: null
          }
        ]}
      />

      {/* Tab Content */}
      <div className="lobby-tab-content">
        {activeTab === 'lobby' ? (
          /* Lobby Tab */
          <div className="lobby-container lobby-main">

          {/* El overlay para la cuenta atrás */}
          {isStarting && (
            <div className="countdown-overlay">
              <div className="countdown-box">
                <Swords size={64} className="countdown-icon" />
                <h1>{t('lobby.game_starting')}</h1>
                <p className="countdown-timer">{countdown}</p>
              </div>
            </div>
          )}

          <div className="lobby-header">
            <h2 className="lobby-title">{lobbyState.name || `Game #${lobbyState.id}`}</h2>

            <p>
              {t('lobby.game_type')} {lobbyState.visibility === "private" ? t('lobby.private') : t('lobby.public')}
            </p>
            {lobbyState.visibility === "private" && (
              <p>
                {t('lobby.access_code')} <strong>{lobbyState.access_code}</strong>
              </p>
            )}
          </div>

          <div className="teams-container">
            {/* Columna Equipo 1 */}
            <div className="team-column">
              <h3>{t('lobby.team_1')}</h3>
              <ul className="player-list">
                {team1.map(player => {
                  const isReady = player.pivot.status_in_game === PlayerStatusInGame.LOBBIED;
                  const isCurrentPlayer = player.id === currentUserPlayer?.id;
                  const showNoUnits = isCurrentPlayer && !hasMinimumUnits;
                  const isHuman = player.type === PlayerType.HUMAN;

                  // Get strategy info for human player (icon + name)
                  const getHumanStrategyInfo = () => {
                    if (appliedLoadoutId) {
                      const template = loadoutTemplates.find(t => t.id === appliedLoadoutId);
                      if (template) {
                        return {
                          icon: getCategoryIcon(template.category),
                          name: template.name,
                        };
                      }
                    }
                    // Use activeCategory directly (custom_unsaved shows as Custom)
                    const meta = getCategoryMeta(activeCategory);
                    return {
                      icon: meta.icon,
                      name: meta.label,
                    };
                  };

                  const humanStrategy = getHumanStrategyInfo();

                  return (
                    <li
                      key={player.id}
                      className={`player-card ${isReady ? 'ready' : 'not-ready'} ${isCurrentPlayer ? 'player-card--current' : ''}`}
                      onClick={isCurrentPlayer ? () => setIsHumanConfigModalOpen(true) : undefined}
                      role={isCurrentPlayer ? 'button' : undefined}
                      tabIndex={isCurrentPlayer ? 0 : undefined}
                    >
                      <div className="player-card-main">
                        {player.type === PlayerType.AI ? (
                          <Bot className="player-icon" size={24} />
                        ) : (
                          <User className="player-icon" size={24} />
                        )}
                        <span className="player-name">
                          {isCurrentPlayer && <span className="you-tag">{t('lobby.you_tag')}</span>}
                          {player.name}
                        </span>
                        {showNoUnits ? (
                          <span className="status-badge status-no-units">{t('lobby.no_units')}</span>
                        ) : (
                          <span className={`status-badge ${isReady ? 'status-ready' : 'status-not-ready'}`}>
                            {isReady ? t('lobby.ready') : t('lobby.not_ready')}
                          </span>
                        )}
                      </div>
                      {isCurrentPlayer && (
                        <div className="player-card-strategy">
                          <span className="strategy-badge">
                            <humanStrategy.icon size={12} />
                            {humanStrategy.name}
                          </span>
                          <XhelosTooltip content={t('lobby.configure_loadout')} placement="top" theme="dark" trigger="hover">
                            <Settings className="player-settings-icon" size={16} />
                          </XhelosTooltip>
                        </div>
                      )}
                      {renderPlayerRosterIcons(player.id)}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Separador VS */}
            <div className="vs-separator">
              <span className="vs-text">VS</span>
            </div>

            {/* Columna Equipo 2 */}
            <div className="team-column">
              <h3>{t('lobby.team_2')}</h3>
              <ul className="player-list">
                {team2.map(player => {
                  const isReady = player.pivot.status_in_game === PlayerStatusInGame.LOBBIED;
                  const isCurrentPlayer = player.id === currentUserPlayer?.id;
                  const showNoUnits = isCurrentPlayer && !hasMinimumUnits;
                  const isBot = player.type === PlayerType.AI;

                  // Get strategy info for bot player (icon + name)
                  const getBotStrategyInfo = () => {
                    const combo = currentBotConfig?.unit_combo || 'random';
                    const template = aiLoadoutTemplates.find(t => t.slug === combo);
                    if (template) {
                      const category = template.category || 'random';
                      return {
                        icon: getCategoryIcon(category),
                        name: template.name,
                      };
                    }
                    // Fallback to random
                    const meta = getCategoryMeta('random');
                    return {
                      icon: meta.icon,
                      name: combo.charAt(0).toUpperCase() + combo.slice(1),
                    };
                  };

                  // Get spawn mode label for bot player
                  const getSpawnModeLabel = () => {
                    const spawnMode = currentBotConfig?.spawn_mode || 'grouped';
                    return spawnMode === 'grouped' ? t('lobby.grouped') : t('lobby.scattered');
                  };

                  // Get strategy info for human player (if team 2 has human)
                  const getHumanStrategyInfo = () => {
                    if (appliedLoadoutId) {
                      const template = loadoutTemplates.find(t => t.id === appliedLoadoutId);
                      if (template) {
                        return {
                          icon: getCategoryIcon(template.category),
                          name: template.name,
                        };
                      }
                    }
                    // Use activeCategory directly (custom_unsaved shows as Custom)
                    const meta = getCategoryMeta(activeCategory);
                    return {
                      icon: meta.icon,
                      name: meta.label,
                    };
                  };

                  const botStrategy = getBotStrategyInfo();
                  const humanStrategy = getHumanStrategyInfo();

                  return (
                    <li
                      key={player.id}
                      className={`player-card ${isReady ? 'ready' : 'not-ready'} ${isBot ? 'player-card--bot' : ''} ${isCurrentPlayer ? 'player-card--current' : ''}`}
                      onClick={isBot ? () => setIsBotConfigModalOpen(true) : (isCurrentPlayer ? () => setIsHumanConfigModalOpen(true) : undefined)}
                      role={isBot || isCurrentPlayer ? 'button' : undefined}
                      tabIndex={isBot || isCurrentPlayer ? 0 : undefined}
                    >
                      <div className="player-card-main">
                        {isBot ? (
                          <Bot className="player-icon" size={24} />
                        ) : (
                          <User className="player-icon" size={24} />
                        )}
                        <span className="player-name">
                          {isCurrentPlayer && <span className="you-tag">{t('lobby.you_tag')}</span>}
                          {player.name}
                        </span>
                        {showNoUnits ? (
                          <span className="status-badge status-no-units">{t('lobby.no_units')}</span>
                        ) : (
                          <span className={`status-badge ${isReady ? 'status-ready' : 'status-not-ready'}`}>
                            {isReady ? t('lobby.ready') : t('lobby.not_ready')}
                          </span>
                        )}
                      </div>
                      {isBot && (
                        <div className="player-card-strategy">
                          <span className="strategy-badge">
                            <botStrategy.icon size={12} />
                            {botStrategy.name}
                          </span>
                          <span className="strategy-badge strategy-badge--secondary">{getSpawnModeLabel()}</span>
                          <XhelosTooltip content={t('lobby.configure_bot')} placement="top" theme="dark" trigger="hover">
                            <Settings className="bot-settings-icon" size={16} />
                          </XhelosTooltip>
                        </div>
                      )}
                      {isCurrentPlayer && !isBot && (
                        <div className="player-card-strategy">
                          <span className="strategy-badge">
                            <humanStrategy.icon size={12} />
                            {humanStrategy.name}
                          </span>
                          <XhelosTooltip content={t('lobby.configure_loadout')} placement="top" theme="dark" trigger="hover">
                            <Settings className="player-settings-icon" size={16} />
                          </XhelosTooltip>
                        </div>
                      )}
                      {renderPlayerRosterIcons(player.id)}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Acciones del Lobby */}
          <div className="lobby-actions">
            {isCreator && (
              <button onClick={handleStartGameClick} disabled={!canStartGame} className="button button-cta button-cta-combo" data-sfx="button">
                {t('lobby.start_game')}
              </button>
            )}

            {currentUserPlayer && (
              <button
                onClick={handleReadyClick}
                disabled={!hasMinimumUnits}
                className={`button ${!hasMinimumUnits ? 'disabled not-ready-button' : (currentUserPlayer.pivot.status_in_game === PlayerStatusInGame.LOBBIED ? 'ready-button' : 'not-ready-button')}`}
                data-sfx="button"
              >
                {!hasMinimumUnits
                  ? t('lobby.im_ready')
                  : (currentUserPlayer.pivot.status_in_game === PlayerStatusInGame.LOBBIED ? t('lobby.im_not_ready') : t('lobby.im_ready'))
                }
              </button>
            )}
          </div>

          {!isCreator && <p className="waiting-message">{t('lobby.waiting_for_creator')}</p>}
          {isCreator && !hasMinimumUnits && (
            <p className="waiting-message">{t('lobby.must_purchase_unit')}</p>
          )}
          {isCreator && hasMinimumUnits && !allHumanPlayersReady && (
            <p className="waiting-message">{t('lobby.waiting_for_players_ready')}</p>
          )}
        </div>
        ) : (
          /* Shop Tab */
          <div className="shop-container">
            {/* Left Side: Shop Items + Purchased Units */}
            <div className="shop-left-panel section">
              {/* Loadout Selector - now includes header with cart + money */}
              <LoadoutSelector
                loadoutTemplates={loadoutTemplates}
                activeCategory={activeCategory}
                appliedLoadoutId={appliedLoadoutId}
                purchasedUnits={purchasedUnits}
                onApplyLoadout={handleApplyLoadout}
                onRegenerateRandom={handleRegenerateRandom}
                onCreateLoadout={handleCreateLoadout}
                onUpdateLoadout={handleUpdateLoadout}
                onDeleteLoadout={handleDeleteLoadout}
                isApplying={isApplyingLoadout}
                isLoading={isLoadoutLoading}
                isCreatingLoadout={isCreatingLoadout}
                isUpdatingLoadout={isUpdatingLoadout}
                isDeletingLoadout={isDeletingLoadout}
                maxCustomTemplates={maxCustomTemplates}
                disabled={isShopLoading}
                // Editable mode for shop view
                editable={true}
                onRemoveUnit={handleRemoveOneUnit}
                onClearAll={handleRefundAllUnits}
                unitCount={purchasedUnits.length}
                maxUnits={maxUnits}
                isRemoving={isRefunding}
                money={playerMoney}
              />

              {/* Grid de Templates Disponibles */}
              {isShopLoading ? (
                <div className="loading-message">{t('lobby.loading_shop')}</div>
              ) : (
                <div className="shop-grid">
                  {availableTemplates.map((templateData) => {
                    const isSelected = selectedUnitTemplate?.id === templateData.unit_template.id;
                    const canAfford = playerMoney >= templateData.price;
                    const canBuy = canAfford && !isAtMaxUnits;
                    return (
                      <div
                        key={templateData.unit_template.id}
                        onClick={() => setSelectedUnitTemplate(templateData.unit_template)}
                        className={`shop-item ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="shop-item-content">
                          <UnitImage
                            unitTemplate={templateData.unit_template}
                            size="medium"
                          />
                          <div className="shop-item-name">
                            {templateData.unit_template.name}
                          </div>
                          <div className={`shop-item-price ${!canAfford ? 'shop-item-price--expensive' : ''}`}>
                            <Coins size={16} />
                            <span>${templateData.price}</span>
                          </div>
                        </div>
                        <div className="shop-item-buy-container">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              !isBuying && canBuy && handleBuyUnit(templateData.unit_template.id);
                            }}
                            disabled={isBuying || !canBuy}
                            className="shop-item-buy-button"
                            data-sfx="button"
                          >
                            {isBuying ? <Loader2 size={16} className="spinner" /> : t('lobby.buy')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Purchased units are now shown in LoadoutSelector with editable mode */}
            </div>

            {/* Right Side: Unit Info Panel */}
            {selectedUnitTemplate && (
              <div className="unit-info-panel section">
                {/* Cost Badges */}
                {(() => {
                  const price = availableTemplates.find(t => t.unit_template.id === selectedUnitTemplate.id)?.price || 0;
                  const canAfford = playerMoney >= price;
                  return (
                    <div className="unit-costs-badges">
                      <div className="cost-badge cost-badge-gold" data-affordable={canAfford}>
                        <span>${price}</span>
                      </div>
                      <div className="cost-badge cost-badge-energy">
                        <Zap size={16} />
                        <span>{selectedUnitTemplate.energy_deploy_cost}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Unit Header */}
                <div className="unit-info-header">
                  <div className="unit-info-icon">
                    <UnitImage
                      unitTemplate={selectedUnitTemplate}
                      size="extrabig-responsive"
                    />
                  </div>
                  <div className="unit-info-text">
                    <h3>{selectedUnitTemplate.name}</h3>
                    {selectedUnitTemplate.description && (
                      <p>{selectedUnitTemplate.description}</p>
                    )}
                  </div>
                </div>

                {/* Stats Grid - 3 rows organized */}
                <div className="shop-stats-grid">
                  {/* Row 1: Vitals (HP, AP) */}
                  <div className="shop-stats-row">
                    <ShopStatDisplay statName="max_hp" value={selectedUnitTemplate.max_hp} icon={<Heart size={14} />} />
                    <ShopStatDisplay statName="max_ap" value={selectedUnitTemplate.max_ap} icon={<Zap size={14} />} />
                  </div>
                  {/* Row 2: Combat (STR, ARM, PSI, WILL) */}
                  <div className="shop-stats-row">
                    <ShopStatDisplay statName="strength" value={selectedUnitTemplate.strength} icon={<STAT_ICONS.strength size={14} />} />
                    <ShopStatDisplay statName="armor" value={selectedUnitTemplate.armor} icon={<STAT_ICONS.armor size={14} />} />
                    <ShopStatDisplay statName="psi" value={selectedUnitTemplate.psi} icon={<STAT_ICONS.psi size={14} />} />
                    <ShopStatDisplay statName="will" value={selectedUnitTemplate.will} icon={<STAT_ICONS.will size={14} />} />
                  </div>
                  {/* Row 3: Utility (SPD, MOV, VIS) */}
                  <div className="shop-stats-row">
                    <ShopStatDisplay statName="agility" value={selectedUnitTemplate.agility} icon={<STAT_ICONS.agility size={14} />} />
                    <ShopStatDisplay statName="max_movement" value={selectedUnitTemplate.max_movement} icon={<STAT_ICONS.movement size={14} />} />
                    <ShopStatDisplay statName="vision" value={selectedUnitTemplate.vision} icon={<STAT_ICONS.vision size={14} />} />
                  </div>
                </div>

                {/* Abilities Section - Actions + Effects combined */}
                {((selectedUnitTemplate.actions && selectedUnitTemplate.actions.length > 0) ||
                  (selectedUnitTemplate.effects && selectedUnitTemplate.effects.length > 0)) && (
                  <div className="shop-abilities-section">
                    <div className="shop-abilities-header">
                      <Bolt size={14} />
                      <span>{t('lobby.abilities')}</span>
                    </div>
                    <div className="shop-abilities-list">
                      {/* Actions */}
                      {selectedUnitTemplate.actions?.map((action) => {
                        const details = formatActionDetails(action, selectedUnitTemplate);
                        const actionTag = parseActionTag(action);
                        const isExpanded = expandedActionIds.has(action.id);

                        const getActionIcon = () => {
                          if (action.category === 'range_damage' || action.category === 'melee_damage') {
                            return <Flame size={14} />;
                          } else if (action.category === 'status') {
                            return <STAT_ICONS.psi size={14} />;
                          } else if (action.category === 'movement') {
                            return <STAT_ICONS.movement size={14} />;
                          }
                          return <Swords size={14} />;
                        };

                        return (
                          <div key={`action-${action.id}`} className={`shop-ability-item shop-ability-item--action ${isExpanded ? 'expanded' : ''}`}>
                            <button className="shop-ability-header" onClick={() => toggleAction(action.id)}>
                              <span className="shop-ability-chevron">
                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </span>
                              <span className="shop-ability-icon">{getActionIcon()}</span>
                              <span className="shop-ability-name">{details.name}</span>
                              <span className="shop-ability-desc">{details.description || ''}</span>
                              {actionTag && (
                                <span className="shop-ability-damage" data-type={actionTag.type}>
                                  {renderIconTag(actionTag.iconKey, actionTag.label, 'shop')}
                                </span>
                              )}
                            </button>
                            {isExpanded && (() => {
                              const aoeInfo = getAoeInfo(action);
                              const castMode = action.target_type === 'self' ? 'Self' :
                                               action.target_type === 'ally' ? 'Ally' :
                                               action.target_type === 'enemy' ? 'Enemy' :
                                               action.target_type === 'move' ? 'Tile' :
                                               action.target_type === 'range' ? 'Range' :
                                               action.target_type === 'melee' ? 'Melee' : action.target_type;
                              return (
                                <div className="shop-ability-details">
                                  <div className="shop-ability-stats">
                                    {details.pa > 0 && <span className="shop-ability-stat"><Target size={14} /> AP: {details.pa}</span>}
                                    {details.energy > 0 && <span className="shop-ability-stat"><Zap size={14} /> Energy: {details.energy}</span>}
                                    {details.cooldown > 0 && <span className="shop-ability-stat"><Timer size={14} /> CD: {details.cooldown}</span>}
                                    {details.range && <span className="shop-ability-stat"><Radio size={14} /> Range: {details.range}</span>}
                                    <span className="shop-ability-stat"><Crosshair size={14} /> Cast: {castMode}</span>
                                    {aoeInfo.isAoe && (
                                      <span className={`shop-ability-stat shop-ability-aoe ${aoeInfo.isBeneficial ? 'beneficial' : 'harmful'}`}>
                                        <Users size={14} /> AOE {aoeInfo.aoeRange}
                                      </span>
                                    )}
                                  </div>
                                  {actionTag && actionTag.type === 'damage' && (
                                    <div className="shop-ability-damage-info">{getDamageTagTooltip({ base: actionTag.base, scaling: actionTag.scaling, damageType: actionTag.damageType })}</div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}

                      {/* Effects (Passives) */}
                      {selectedUnitTemplate.effects?.map((effect) => {
                        const details = formatEffectDetails(effect);
                        const isExpanded = expandedEffectIds.has(effect.id);

                        return (
                          <div key={`effect-${effect.id}`} className={`shop-ability-item shop-ability-item--effect ${isExpanded ? 'expanded' : ''}`}>
                            <button className="shop-ability-header" onClick={() => toggleEffect(effect.id)}>
                              <span className="shop-ability-chevron">
                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </span>
                              <span className="shop-ability-icon"><Shield size={14} /></span>
                              <span className="shop-ability-name">{details.name}</span>
                              <span className="shop-ability-desc">{details.description || ''}</span>
                              {effect.is_passive && <span className="shop-ability-badge">{t('lobby.passive')}</span>}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Buy Button */}
                {(() => {
                  const price = availableTemplates.find(t => t.unit_template.id === selectedUnitTemplate.id)?.price || 0;
                  const canAfford = playerMoney >= price;
                  return (
                    <button
                      onClick={() => !isBuying && canAfford && handleBuyUnit(selectedUnitTemplate.id)}
                      disabled={isBuying || !canAfford}
                      className="button button-cta"
                      data-sfx="button"
                    >
                      {isBuying ? <Loader2 size={20} className="spinner" /> : t('lobby.buy_for', { price })}
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bot Config Modal */}
      <BotConfigModal
        isOpen={isBotConfigModalOpen}
        onClose={() => setIsBotConfigModalOpen(false)}
        currentConfig={currentBotConfig}
        loadoutTemplates={aiLoadoutTemplates}
        botRoster={aiPlayer ? playerRosters[aiPlayer.id] || [] : []}
        onSave={handleBotConfigSave}
        onRegenerate={handleBotConfigRegenerate}
        isSaving={isUpdatingBotConfig}
        isRegenerating={isRegenerating}
        isLoadingTemplates={isAILoadoutLoading}
        isCreator={isCreator}
      />

      {/* Human Config Modal (Loadout) */}
      <HumanConfigModal
        isOpen={isHumanConfigModalOpen}
        onClose={() => setIsHumanConfigModalOpen(false)}
        loadoutTemplates={loadoutTemplates}
        activeCategory={activeCategory}
        appliedLoadoutId={appliedLoadoutId}
        purchasedUnits={purchasedUnits}
        onApplyLoadout={handleApplyLoadout}
        onRegenerateRandom={handleRegenerateRandom}
        onSwitchToCustom={handleSwitchToCustom}
        onCreateLoadout={handleCreateLoadout}
        onUpdateLoadout={handleUpdateLoadout}
        onDeleteLoadout={handleDeleteLoadout}
        isApplying={isApplyingLoadout}
        isSwitchingToCustom={isSwitchingToCustom}
        isLoadingTemplates={isLoadoutLoading}
        isCreatingLoadout={isCreatingLoadout}
        isUpdatingLoadout={isUpdatingLoadout}
        isDeletingLoadout={isDeletingLoadout}
        maxCustomTemplates={maxCustomTemplates}
        onGoToShop={() => handleTabChange('shop')}
      />
    </div>
  );
}

export default LobbyView;